import React, { useState, useRef, useEffect } from "react";
import { Stack, Typography } from "@mui/material";
import { FileInputProps } from ".";
import { DamsAppAsset } from "../../assets/registry";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_FILE_TYPES = ["image/jpeg", "image/jpg", "image/png"];

const FileInput: React.FC<FileInputProps> = ({
  label = "Images",
  size = "short",
  multiple = false,
  accept = "image/*",
  onFileSelect,
  resetFiles = false,
  onResetComplete = () => {},
  maxFileSize = MAX_FILE_SIZE,
  allowedTypes = ALLOWED_FILE_TYPES,
  error,
  errorMessage,
  name,
  required = false,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const InputSize =
    size === "long"
      ? { width: 512 }
      : size === "full"
      ? { width: "100%" }
      : { width: 260 };

  useEffect(() => {
    if (resetFiles) {
      setFiles([]);
      setValidationError("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      onResetComplete();
    }
  }, [resetFiles, onResetComplete]);

  const validateFiles = (selectedFiles: File[]) => {
    const validFiles: File[] = [];
    let hasError = false;

    for (const file of selectedFiles) {
      if (file.size > maxFileSize) {
        setValidationError(`File "${file.name}" exceeds the 5MB size limit.`);
        hasError = true;
        break;
      }

      if (!file.type.startsWith("image/")) {
        setValidationError(`File "${file.name}" is not an image.`);
        hasError = true;
        break;
      }

      validFiles.push(file);
    }

    if (!hasError) {
      setValidationError("");
      return validFiles;
    }

    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const validFiles = validateFiles(selectedFiles);

    if (validFiles) {
      setFiles(validFiles);
      if (onFileSelect) {
        onFileSelect(multiple ? validFiles : validFiles[0] || null);
      }
    } else if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.startsWith("image/")
    );

    const validFiles = validateFiles(droppedFiles);

    if (validFiles && validFiles.length > 0) {
      setFiles(validFiles);
      if (onFileSelect) {
        onFileSelect(multiple ? validFiles : validFiles[0] || null);
      }
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const removeFile = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
    if (onFileSelect) {
      onFileSelect(
        multiple ? newFiles : newFiles.length > 0 ? newFiles[0] : null
      );
    }
  };

  return (
    <Stack width={InputSize.width} gap={0.3}>
      <Typography
        fontFamily={"Inter"}
        fontWeight={500}
        fontSize={14}
        color={"#344054"}
      >
        {label}
        {required && <span style={{ color: "red", marginLeft: "4px" }}>*</span>}
      </Typography>

      <div style={fileStyles.fileInputContainer}>
        <div
          style={{
            ...fileStyles.fileInputArea,
            ...(isDragging ? fileStyles.dragging : {}),
          }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={triggerFileInput}
        >
          <input
            type="file"
            ref={fileInputRef}
            style={fileStyles.hiddenInput}
            onChange={handleFileChange}
            accept={accept}
            multiple={multiple}
            name={name}
            required={required}
            aria-label="File upload"
          />
          <img
            src={DamsAppAsset.icons.image_upload_icon}
            alt="upload icon"
            style={fileStyles.uploadIcon}
          />
          <Typography style={fileStyles.uploadText}>
            {isDragging
              ? "Drop images here"
              : "Drag & drop images here or click to browse"}
          </Typography>
          <Typography style={fileStyles.uploadHint}>
            Supported formats: JPG, JPEG, PNG | Max size: 5MB per file
          </Typography>
        </div>

        {(validationError || (error && errorMessage)) && (
          <div style={fileStyles.errorContainer}>
            <Typography style={fileStyles.errorText}>
              {validationError || errorMessage}
            </Typography>
          </div>
        )}

        {files.length > 0 && (
          <div style={fileStyles.filePreview}>
            {files.map((file, index) => (
              <div key={index} style={fileStyles.fileItem}>
                <div style={fileStyles.fileThumbnail}>
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Preview ${index}`}
                    style={fileStyles.thumbnailImage}
                  />
                </div>
                <div style={fileStyles.fileDetails}>
                  <Typography style={fileStyles.fileName}>
                    {file.name}
                  </Typography>
                  <Typography style={fileStyles.fileSize}>
                    {(file.size / 1024).toFixed(1)} KB
                  </Typography>
                </div>
                <div
                  style={fileStyles.removeButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(index);
                  }}
                  aria-label={`Remove ${file.name}`}
                >
                  <img src={DamsAppAsset.icons.close} alt="upload icon" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Stack>
  );
};

const fileStyles = {
  fileInputContainer: {
    marginBottom: "30px",
    width: "100%",
  },
  fileInputArea: {
    border: "1px solid #D0D5DD",
    borderRadius: "8px",
    padding: "15px",
    textAlign: "center" as const,
    cursor: "pointer",
    transition: "border-color 0.3s, background-color 0.3s",
    marginBottom: "15px",
  },
  dragging: {
    borderColor: "#667085",
    backgroundColor: "rgba(0, 128, 0, 0.055)",
  },
  hiddenInput: {
    display: "none",
  },
  uploadIcon: {
    marginBottom: "10px",
    width: "25px",
    height: "25px",
    display: "block",
    marginLeft: "auto",
    marginRight: "auto",
  },
  uploadText: {
    fontSize: "0.95rem",
    color: "#667085",
    marginBottom: "5px",
    fontWeight: 500,
    margin: "0 0 5px 0",
  },
  uploadHint: {
    fontSize: "0.95rem",
    color: "#98A2B3",
    fontWeight: 500,
    margin: 0,
  },
  errorContainer: {
    backgroundColor: "#FEF2F2",
    padding: "10px 15px",
    marginBottom: "5px",
    borderRadius: "8px",
    textAlign: "center" as const,
  },
  errorText: {
    color: "#FE0000",
    fontSize: "0.9rem",
    fontWeight: 550,
    margin: 0,
  },
  filePreview: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
    gap: "15px",
    marginTop: "15px",
  },
  fileItem: {
    border: "1px solid #D0D5DD",
    borderRadius: "8px",
    overflow: "hidden",
    position: "relative" as const,
    backgroundColor: "#FFFFFF",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
  },
  fileThumbnail: {
    height: "100px",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderBottom: "1px solid #D0D5DD",
  },
  thumbnailImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
  },
  fileDetails: {
    padding: "8px",
  },
  fileName: {
    fontSize: "0.8rem",
    whiteSpace: "nowrap" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontWeight: 500,
    color: "#344054",
    marginBottom: "4px",
    display: "block",
  },
  fileSize: {
    color: "#667085",
    fontSize: "0.75rem",
  },
  removeButton: {
    position: "absolute" as const,
    top: "5px",
    right: "5px",
    width: "20px",
    height: "20px",
    padding: "2px",
    borderRadius: "20px",
    backgroundColor: "#ebebeb",
    border: "none",
    fontSize: "1.4rem",
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
};

export default FileInput;
